<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div style="display:flex;align-items:center;gap:10px">
        <router-link to="/super-admin/customers" class="gl-back-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </router-link>
        <div>
          <h1 class="gl-page-title">{{ customer?.name || 'Customer Details' }}</h1>
          <div class="gl-breadcrumb">
            <router-link to="/" class="gl-breadcrumb-link">Home</router-link>
            <span class="gl-breadcrumb-sep">/</span>
            <router-link to="/super-admin/customers" class="gl-breadcrumb-link">Customers</router-link>
            <span class="gl-breadcrumb-sep">/</span>
            <span>{{ customer?.name || '…' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Skeleton ── -->
    <div v-if="loading">
      <div class="gl-card gl-hero-card">
        <div class="gl-hero-body">
          <div style="display:flex;align-items:center;gap:16px;flex:1;min-width:0">
            <div class="gl-skel" style="width:64px;height:64px;border-radius:50%;flex-shrink:0"></div>
            <div style="flex:1">
              <div class="gl-skel" style="height:18px;width:50%;border-radius:8px;margin-bottom:8px"></div>
              <div class="gl-skel" style="height:12px;width:70%;border-radius:6px"></div>
            </div>
          </div>
          <div class="gl-skel" style="width:64px;height:24px;border-radius:20px;flex-shrink:0"></div>
        </div>
      </div>
      <div class="gl-stats-grid">
        <div v-for="i in 4" :key="i" class="gl-stat-card">
          <div class="gl-skel" style="width:40px;height:40px;border-radius:10px;flex-shrink:0"></div>
          <div style="flex:1">
            <div class="gl-skel" style="height:10px;width:60%;border-radius:6px;margin-bottom:7px"></div>
            <div class="gl-skel" style="height:20px;width:50%;border-radius:6px;margin-bottom:8px"></div>
            <div class="gl-skel" style="height:26px;width:110px;border-radius:8px"></div>
          </div>
        </div>
      </div>
      <div class="gl-wallet-stats-grid">
        <div v-for="i in 3" :key="i" class="gl-wallet-stat-card">
          <div class="gl-skel" style="height:10px;width:70%;border-radius:6px;margin-bottom:7px"></div>
          <div class="gl-skel" style="height:20px;width:50%;border-radius:6px"></div>
        </div>
      </div>
    </div>

    <!-- ── Content ── -->
    <div v-else-if="customer">

      <!-- Hero card -->
      <div class="gl-card gl-hero-card">
        <div class="gl-hero-body">
          <div class="gl-hero-left">
            <div class="gl-cust-avatar-hero" v-if="customer.avatar">
              <img :src="customer.avatar" :alt="customer.name" />
            </div>
            <div class="gl-cust-avatar-hero gl-cust-initial-hero" v-else>
              {{ (customer.name || '?').charAt(0).toUpperCase() }}
            </div>
            <div class="gl-hero-info">
              <h2 class="gl-hero-name">{{ customer.name || 'No Name' }}</h2>
              <div class="gl-hero-meta">
                <span v-if="customer.phone" class="gl-hero-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.51 5.51l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  {{ customer.phone }}
                </span>
                <span v-if="customer.email" class="gl-hero-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  {{ customer.email }}
                </span>
                <span v-if="customer.phone_verified_at" class="gl-hero-meta-item gl-verified-chip">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Verified
                </span>
              </div>
              <span v-if="customer.membership_tier" class="gl-tier-badge" style="margin-top:8px;display:inline-block">
                {{ customer.membership_tier.name }}
              </span>
            </div>
          </div>
          <div class="gl-hero-right">
            <span :class="customer.is_active ? 'gl-badge gl-badge-active' : 'gl-badge gl-badge-inactive'" style="font-size:12px;padding:4px 12px">
              {{ customer.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>
        </div>
      </div>

      <!-- 4 stat cards -->
      <div class="gl-stats-grid">
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-green">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
          </div>
          <div style="flex:1;min-width:0">
            <div class="gl-stat-label">Wallet Balance</div>
            <div class="gl-stat-value" :class="customer.wallet_balance > 0 ? 'gl-val-green' : ''">
              RM {{ formatCurrency(customer.wallet_balance) }}
            </div>
            <button class="gl-adjust-btn" @click="showAdjustModal = true">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Adjust Balance
            </button>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-amber">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Points Balance</div>
            <div class="gl-stat-value">{{ customer.points_balance || 0 }}</div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-teal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Total Orders</div>
            <div class="gl-stat-value">{{ customer.order_stats?.total_orders || 0 }}</div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Total Spent</div>
            <div class="gl-stat-value">RM {{ formatCurrency(customer.order_stats?.total_spent) }}</div>
          </div>
        </div>
      </div>

      <!-- Wallet summary strip -->
      <div class="gl-wallet-stats-grid">
        <div class="gl-wallet-stat-card">
          <div class="gl-ws-label">Total Top-ups</div>
          <div class="gl-ws-value gl-ws-green">RM {{ formatCurrency(customer.wallet_stats?.total_topups) }}</div>
        </div>
        <div class="gl-wallet-stat-card">
          <div class="gl-ws-label">Spent (Wallet)</div>
          <div class="gl-ws-value gl-ws-red">RM {{ formatCurrency(customer.wallet_stats?.total_spent) }}</div>
        </div>
        <div class="gl-wallet-stat-card">
          <div class="gl-ws-label">Total Refunds</div>
          <div class="gl-ws-value gl-ws-blue">RM {{ formatCurrency(customer.wallet_stats?.total_refunds) }}</div>
        </div>
      </div>

      <!-- Bottom two-col: orders by company + recent orders -->
      <div class="gl-bottom-grid">
        <!-- Orders by company -->
        <div class="gl-card">
          <div class="gl-card-header">Orders by Company</div>
          <div class="gl-card-body-pad">
            <div v-if="customer.orders_by_company?.length">
              <div v-for="(company, i) in customer.orders_by_company" :key="i" class="gl-company-row">
                <div>
                  <div class="gl-company-name">{{ company.name }}</div>
                  <div class="gl-company-sub">{{ company.orders_count }} orders</div>
                </div>
                <div class="gl-company-spent">RM {{ formatCurrency(company.total_spent) }}</div>
              </div>
            </div>
            <div v-else class="gl-empty-msg" style="padding:24px 0;text-align:left">No orders yet</div>
          </div>
        </div>

        <!-- Recent orders table -->
        <div class="gl-card">
          <div class="gl-card-header">Recent Orders</div>
          <div v-if="!customer.recent_orders?.length" class="gl-empty-msg" style="padding:32px 0">No orders yet</div>
          <div v-else class="gl-tbl-scroll">
            <table class="gl-tbl">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Company</th>
                  <th>Outlet</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="order in customer.recent_orders" :key="order.id">
                  <td class="gl-td-mono">{{ order.order_number }}</td>
                  <td class="gl-td-bold">{{ order.company }}</td>
                  <td class="gl-td-muted">{{ order.outlet }}</td>
                  <td class="gl-td-bold">RM {{ formatCurrency(order.total) }}</td>
                  <td><span :class="getStatusBadge(order.status)">{{ order.status }}</span></td>
                  <td class="gl-td-muted">{{ formatDate(order.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Wallet transactions -->
      <div class="gl-card">
        <div class="gl-card-header">Recent Wallet Transactions</div>
        <div v-if="!customer.recent_transactions?.length" class="gl-empty-msg" style="padding:32px 0">No transactions yet</div>
        <div v-else class="gl-tbl-scroll">
          <table class="gl-tbl">
            <thead>
              <tr>
                <th>Type</th>
                <th>Amount</th>
                <th>Balance After</th>
                <th>Description</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tx in customer.recent_transactions" :key="tx.id">
                <td><span :class="getTransactionBadge(tx.type)">{{ tx.type }}</span></td>
                <td :class="tx.amount > 0 ? 'gl-tx-pos' : 'gl-tx-neg'">
                  {{ tx.amount > 0 ? '+' : '' }}RM {{ formatCurrency(Math.abs(tx.amount)) }}
                </td>
                <td class="gl-td-muted">RM {{ formatCurrency(tx.balance_after) }}</td>
                <td class="gl-td-muted">{{ tx.description || '—' }}</td>
                <td class="gl-td-muted">{{ formatDate(tx.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Not found -->
    <div v-else class="gl-empty-msg" style="padding:60px 0">Customer not found.</div>

    <!-- Adjust Balance Modal -->
    <div v-if="showAdjustModal" class="gl-modal-overlay" @click.self="showAdjustModal = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <h2 class="gl-modal-title">Adjust Wallet Balance</h2>
          <button class="gl-modal-close" @click="showAdjustModal = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-form-group">
            <label class="gl-label">Current Balance</label>
            <input type="text" class="gl-input" :value="'RM ' + formatCurrency(customer?.wallet_balance)" disabled />
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Adjustment Type</label>
            <select v-model="adjustForm.type" class="gl-input gl-select-input">
              <option value="add">Add Balance</option>
              <option value="deduct">Deduct Balance</option>
            </select>
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Amount (RM) <span class="gl-required">*</span></label>
            <input type="number" v-model="adjustForm.amount" class="gl-input" min="0.01" step="0.01" placeholder="Enter amount" />
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Reason <span class="gl-required">*</span></label>
            <textarea v-model="adjustForm.reason" class="gl-input gl-textarea" rows="3" placeholder="Enter reason for adjustment"></textarea>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-cancel" @click="showAdjustModal = false">Cancel</button>
          <button class="gl-btn-save" @click="adjustBalance" :disabled="adjusting">
            {{ adjusting ? 'Processing…' : 'Adjust Balance' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminCustomerApi, superAdminWalletApi } from '@/services/superAdminApi';

export default {
  name: 'CustomerDetail',
  data() {
    return {
      loading: false,
      adjusting: false,
      customer: null,
      showAdjustModal: false,
      adjustForm: { type: 'add', amount: '', reason: '' }
    };
  },
  created() { this.fetchCustomer(); },
  methods: {
    async fetchCustomer() {
      this.loading = true;
      try {
        const response = await superAdminCustomerApi.get(this.$route.params.id);
        if (response.data.success) this.customer = response.data.data;
      } catch (error) { console.error('Failed to fetch customer:', error); }
      finally { this.loading = false; }
    },
    async adjustBalance() {
      if (!this.adjustForm.amount || !this.adjustForm.reason) { alert('Please fill in all fields'); return; }
      this.adjusting = true;
      try {
        const response = await superAdminWalletApi.adjustBalance(this.$route.params.id, {
          type: this.adjustForm.type,
          amount: parseFloat(this.adjustForm.amount),
          reason: this.adjustForm.reason
        });
        if (response.data.success) {
          this.customer.wallet_balance = response.data.data.new_balance;
          this.showAdjustModal = false;
          this.adjustForm = { type: 'add', amount: '', reason: '' };
          this.fetchCustomer();
        }
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to adjust balance');
      } finally { this.adjusting = false; }
    },
    formatCurrency(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getStatusBadge(status) {
      return { pending: 'gl-badge gl-badge-pending', confirmed: 'gl-badge gl-badge-info', preparing: 'gl-badge gl-badge-preparing', ready: 'gl-badge gl-badge-active', completed: 'gl-badge gl-badge-active', cancelled: 'gl-badge gl-badge-inactive' }[status] || 'gl-badge gl-badge-muted';
    },
    getTransactionBadge(type) {
      return { topup: 'gl-badge gl-badge-active', payment: 'gl-badge gl-badge-info', refund: 'gl-badge gl-badge-pending', adjustment: 'gl-badge gl-badge-amber' }[type] || 'gl-badge gl-badge-muted';
    }
  }
};
</script>

<style scoped>
.gl-page { padding: 24px 28px 48px; }
.gl-page-header { margin-bottom: 20px; }
.gl-page-title { font-size: 22px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.4px; margin: 0 0 4px; }
.gl-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #8a9e9a; }
.gl-breadcrumb-link { color: #900C27; text-decoration: none; }
.gl-breadcrumb-link:hover { text-decoration: underline; }
.gl-breadcrumb-sep { opacity: .5; }
.gl-back-btn { width: 34px; height: 34px; border-radius: 9px; border: 1px solid #e0e7e5; background: #fff; display: flex; align-items: center; justify-content: center; color: #900C27; cursor: pointer; text-decoration: none; flex-shrink: 0; }
.gl-back-btn:hover { background: #f0f3f2; }

/* ── Hero card ── */
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; margin-bottom: 16px; }
.gl-hero-card { margin-bottom: 16px; }
.gl-hero-body { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; flex-wrap: wrap; }
.gl-hero-left { display: flex; align-items: center; gap: 16px; flex: 1; min-width: 0; }
.gl-hero-right { flex-shrink: 0; }
.gl-cust-avatar-hero { width: 64px; height: 64px; border-radius: 50%; overflow: hidden; flex-shrink: 0; }
.gl-cust-avatar-hero img { width: 100%; height: 100%; object-fit: cover; }
.gl-cust-initial-hero { background: rgba(144,12,39,.1); color: #900C27; font-size: 24px; font-weight: 800; display: flex; align-items: center; justify-content: center; }
.gl-hero-info { min-width: 0; }
.gl-hero-name { font-size: 18px; font-weight: 800; color: #1a2e2b; margin: 0 0 6px; }
.gl-hero-meta { display: flex; flex-wrap: wrap; gap: 8px 16px; }
.gl-hero-meta-item { display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: #6b7f7b; }
.gl-verified-chip { color: #16a34a; font-weight: 600; }
.gl-tier-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 20px; background: rgba(59,130,246,.1); color: #1d4ed8; }

/* ── Badges ── */
.gl-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap; text-transform: capitalize; }
.gl-badge-active    { background: #e0f0ec; color: #1a5248; }
.gl-badge-inactive  { background: #fce7e7; color: #dc2626; }
.gl-badge-pending   { background: #fef3c7; color: #92400e; }
.gl-badge-preparing { background: #ede9fe; color: #6d28d9; }
.gl-badge-info      { background: #dbeafe; color: #1d4ed8; }
.gl-badge-muted     { background: #f0f3f2; color: #6b7f7b; }
.gl-badge-amber     { background: #fef3c7; color: #92400e; }

/* ── Stats ── */
.gl-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 14px; }
.gl-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 18px 20px; display: flex; align-items: flex-start; gap: 14px; }
.gl-stat-icon { width: 42px; height: 42px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-icon-teal  { background: rgba(144,12,39,.12); color: #900C27; }
.gl-icon-blue  { background: rgba(59,130,246,.12); color: #3b82f6; }
.gl-icon-green { background: rgba(22,163,74,.12);  color: #16a34a; }
.gl-icon-amber { background: rgba(245,158,11,.12); color: #d97706; }
.gl-stat-label { font-size: 11px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.gl-stat-value { font-size: 20px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.5px; margin-bottom: 8px; }
.gl-val-green { color: #16a34a; }
.gl-adjust-btn { display: flex; align-items: center; gap: 5px; padding: 6px 10px; border: 1px solid #16a34a; border-radius: 7px; color: #16a34a; font-size: 11.5px; font-weight: 600; cursor: pointer; background: transparent; white-space: nowrap; transition: all .15s; }
.gl-adjust-btn:hover { background: rgba(22,163,74,.06); }

/* ── Wallet stats strip ── */
.gl-wallet-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 16px; }
.gl-wallet-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 12px; padding: 16px 20px; }
.gl-ws-label { font-size: 11px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }
.gl-ws-value { font-size: 18px; font-weight: 800; letter-spacing: -0.5px; }
.gl-ws-green { color: #16a34a; }
.gl-ws-red   { color: #dc2626; }
.gl-ws-blue  { color: #3b82f6; }

/* ── Bottom two-col ── */
.gl-bottom-grid { display: grid; grid-template-columns: 300px 1fr; gap: 16px; margin-bottom: 16px; }

/* ── Card header/body ── */
.gl-card-header { padding: 15px 20px 13px; font-size: 14px; font-weight: 700; color: #1a2e2b; border-bottom: 1px solid #f0f3f2; }
.gl-card-body-pad { padding: 16px 20px; }

/* ── Orders by company ── */
.gl-company-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f5f7f6; }
.gl-company-row:last-child { border-bottom: none; }
.gl-company-name { font-size: 13px; font-weight: 600; color: #1a2e2b; }
.gl-company-sub { font-size: 11.5px; color: #8a9e9a; margin-top: 2px; }
.gl-company-spent { font-size: 13px; font-weight: 700; color: #16a34a; flex-shrink: 0; }

/* ── Table ── */
.gl-tbl-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.gl-tbl { width: 100%; border-collapse: collapse; min-width: 480px; }
.gl-tbl thead th { font-size: 11px; font-weight: 700; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; padding: 10px 16px; background: #f8faf9; border-bottom: 1px solid #eef0ef; white-space: nowrap; }
.gl-tbl tbody tr { border-bottom: 1px solid #f5f7f6; transition: background .12s; }
.gl-tbl tbody tr:last-child { border-bottom: none; }
.gl-tbl tbody tr:hover { background: #f9fbfa; }
.gl-tbl tbody td { padding: 11px 16px; font-size: 13px; color: #2a3d3a; }
.gl-td-muted { color: #6b7f7b !important; }
.gl-td-bold { font-weight: 700; color: #1a2e2b !important; }
.gl-td-mono { font-family: 'Menlo','Consolas',monospace; font-size: 12px; color: #900C27 !important; font-weight: 600; }
.gl-tx-pos { font-weight: 700; color: #16a34a !important; }
.gl-tx-neg { font-weight: 700; color: #dc2626 !important; }

/* ── Skeleton ── */
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Empty ── */
.gl-empty-msg { font-size: 13px; color: #a0b5b0; text-align: center; display: flex; flex-direction: column; align-items: center; }

/* ── Modal ── */
.gl-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px; }
.gl-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.18); position: relative; }
.gl-modal-header { padding: 22px 56px 16px 24px; border-bottom: 1px solid #f0f3f2; }
.gl-modal-title { font-size: 17px; font-weight: 800; color: #1a2e2b; margin: 0; }
.gl-modal-close { position: absolute; top: 16px; right: 16px; width: 32px; height: 32px; border-radius: 8px; border: 1px solid #eaedec; background: #fff; color: #6b7f7b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.gl-modal-close:hover { background: #f5f7f6; color: #1a2e2b; }
.gl-modal-body { padding: 22px 24px; display: flex; flex-direction: column; gap: 14px; }
.gl-modal-footer { padding: 16px 24px 22px; border-top: 1px solid #f0f3f2; display: flex; justify-content: flex-end; gap: 10px; }
.gl-form-group { display: flex; flex-direction: column; gap: 6px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #2a3d3a; }
.gl-required { color: #dc2626; }
.gl-input { padding: 9px 12px; border: 1px solid #e0e7e5; border-radius: 9px; font-size: 13px; color: #2a3d3a; background: #fff; outline: none; transition: border-color .15s; width: 100%; box-sizing: border-box; }
.gl-input:focus { border-color: #900C27; }
.gl-input:disabled { background: #f8faf9; color: #8a9e9a; }
.gl-select-input { cursor: pointer; }
.gl-textarea { resize: vertical; min-height: 72px; }
.gl-btn-cancel { padding: 9px 18px; border: 1px solid #e0e7e5; border-radius: 9px; background: #fff; color: #6b7f7b; font-size: 13px; font-weight: 600; cursor: pointer; }
.gl-btn-cancel:hover { background: #f5f7f6; }
.gl-btn-save { padding: 9px 22px; background: #900C27; color: #fff; border: none; border-radius: 9px; font-size: 13px; font-weight: 700; cursor: pointer; transition: background .15s; }
.gl-btn-save:hover:not(:disabled) { background: #1a5248; }
.gl-btn-save:disabled { opacity: .6; cursor: not-allowed; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-header { margin-bottom: 14px; }
  .gl-breadcrumb { display: none; }

  .gl-hero-body { padding: 16px; flex-direction: column; align-items: flex-start; }
  .gl-hero-right { align-self: flex-start; }
  .gl-hero-name { font-size: 16px; }
  .gl-cust-avatar-hero { width: 52px; height: 52px; }

  .gl-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .gl-stat-card { padding: 14px; gap: 10px; align-items: flex-start; }
  .gl-stat-icon { width: 36px; height: 36px; border-radius: 9px; }
  .gl-stat-value { font-size: 16px; margin-bottom: 6px; }

  .gl-wallet-stats-grid { grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .gl-wallet-stat-card { padding: 12px 14px; }
  .gl-ws-value { font-size: 14px; }

  .gl-bottom-grid { grid-template-columns: 1fr; gap: 0; margin-bottom: 0; }
  .gl-bottom-grid > .gl-card { margin-bottom: 16px; }

  .gl-modal { border-radius: 16px 16px 0 0; position: fixed; bottom: 0; left: 0; right: 0; max-width: 100%; max-height: 90vh; }
  .gl-modal-overlay { align-items: flex-end; padding: 0; }
}

@media (max-width: 480px) {
  .gl-wallet-stats-grid { grid-template-columns: 1fr; }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb-link { color: #5a7470; }
body.dark-only .gl-back-btn { background: #1e2d2b; border-color: #2a3d3a; color: #3d9e8c; }
body.dark-only .gl-back-btn:hover { background: #243432; }
body.dark-only .gl-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-hero-name { color: #e0eded; }
body.dark-only .gl-hero-meta-item { color: #5a7470; }
body.dark-only .gl-cust-initial-hero { background: rgba(144,12,39,.2); }
body.dark-only .gl-stat-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-stat-label { color: #5a7470; }
body.dark-only .gl-stat-value { color: #e0eded; }
body.dark-only .gl-wallet-stat-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-ws-label { color: #5a7470; }
body.dark-only .gl-card-header { color: #e0eded; border-bottom-color: #2a3d3a; }
body.dark-only .gl-company-row { border-bottom-color: #2a3d3a; }
body.dark-only .gl-company-name { color: #e0eded; }
body.dark-only .gl-company-sub { color: #5a7470; }
body.dark-only .gl-tbl thead th { background: #16211f; color: #5a7470; border-bottom-color: #2a3d3a; }
body.dark-only .gl-tbl tbody tr { border-bottom-color: #222f2d; }
body.dark-only .gl-tbl tbody tr:hover { background: #1c2f2b; }
body.dark-only .gl-tbl tbody td { color: #c0d4d0; }
body.dark-only .gl-td-muted { color: #5a7470 !important; }
body.dark-only .gl-td-bold { color: #e0eded !important; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-empty-msg { color: #5a7470; }
body.dark-only .gl-modal { background: #1e2d2b; }
body.dark-only .gl-modal-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-modal-title { color: #e0eded; }
body.dark-only .gl-modal-close { background: #1e2d2b; border-color: #2a3d3a; color: #5a7470; }
body.dark-only .gl-modal-close:hover { background: #243432; color: #e0eded; }
body.dark-only .gl-modal-footer { border-top-color: #2a3d3a; }
body.dark-only .gl-label { color: #c0d4d0; }
body.dark-only .gl-input { background: #16211f; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-input:focus { border-color: #3d9e8c; }
body.dark-only .gl-input:disabled { background: #1a2927; color: #5a7470; }
body.dark-only .gl-btn-cancel { background: #1e2d2b; border-color: #2a3d3a; color: #c0d4d0; }
body.dark-only .gl-btn-cancel:hover { background: #243432; }
</style>
